import { Button, Flex, Heading, Stack, Text, VStack } from '@chakra-ui/react'
import type { ComponentCampaignCurrentNft } from '@twlvxtwlv/cms'
import type { ProductVariant } from '@twlvxtwlv/tokenforge'
import { toLocalizedEuro } from '@twlvxtwlv/utils/src/localization'
import { useTranslation } from 'next-i18next'
import NextLink from 'next/link'
import { useEffect, useState } from 'react'
import type { ProductAvailability, ProductType } from '@twlvxtwlv/types'
import { getTokenAvailabilityText } from '@twlvxtwlv/utils/src/token'

interface ProductsLeftCtaProps {
  campaignId: string | null
  currentNft: ComponentCampaignCurrentNft
  variant: ProductVariant | undefined | null
  priceInEuroCents: number
  availability: ProductAvailability
  productIsPurchasable: boolean
  onPurchaseButtonClicked: () => void
  onLearnMoreButtonClicked: () => void
  productType: ProductType
  onOpenNotifyMeModal: () => void
}

export function ProductsLeftCta({
  campaignId,
  currentNft,
  priceInEuroCents,
  availability,
  productIsPurchasable,
  onPurchaseButtonClicked,
  onLearnMoreButtonClicked,
  variant,
  productType,
  onOpenNotifyMeModal,
}: ProductsLeftCtaProps) {
  const { t } = useTranslation(['auction', 'nft-details'])

  const [buyButtonText, setBuyButtonText] = useState<string | null>(null)

  useEffect(() => {
    switch (availability) {
      case 'buyable':
        setBuyButtonText(t('nft-details:buy-campaign'))
        break
      case 'coming-soon':
        setBuyButtonText(t('nft-details:coming-soon'))
        break
      case 'sold-out':
        setBuyButtonText(t('nft-details:sold-out'))
    }
  }, [availability])

  const isProductTicket = productType === 'ticket'
  const isProductAward = productType === 'award'
  const isProductClaimOnly = productType === 'claimOnly'

  return (
    <Flex
      direction={['column', 'column', 'column', 'column', 'row']}
      align="center"
      h="full"
      gap={[4, 8, 12]}
    >
      <Flex
        gap={12}
        direction={['row']}
        align="center"
        justify={['center', 'center', 'center', 'center', 'flex-start']}
        h="full"
      >
        {!isProductTicket ? (
          <>
            <VStack h="full" spacing={0} align="flex-start">
              <Text
                fontWeight="400"
                color="black"
                fontSize={['sm', 'md', 'lg', 'xl']}
                textTransform="uppercase"
              >
                {getTokenAvailabilityText(variant?.onHand, variant?.totalSupply, false)}
              </Text>
              <Text fontSize={['xs', 'xs', 'sm']} fontWeight="400" color="txtDarkGray">
                {isProductAward || isProductClaimOnly
                  ? t('nft-details:total-tokens')
                  : t('nft-details:tokens-left')}
              </Text>
            </VStack>
          </>
        ) : null}

        {productIsPurchasable && !isProductAward && !isProductClaimOnly ? (
          <Stack h="full" spacing={0} align="flex-start">
            <Text
              fontWeight="400"
              color="black"
              fontSize={['sm', 'md', 'lg', 'xl']}
              textTransform="uppercase"
            >
              {toLocalizedEuro(priceInEuroCents)}
            </Text>
            <Heading fontSize={['xs', 'xs', 'md']} fontWeight="400" color="txtDarkGray">
              {t('nft-details:price')}
            </Heading>
          </Stack>
        ) : null}
      </Flex>

      <Flex
        direction={['column', 'column', 'column', 'column', 'row']}
        h="full"
        w="full"
        align="center"
        justify={{ xl: 'flex-end' }}
        gap={[2, 2, 2, 4]}
      >
        {!isProductAward ? (
          <>
            {availability !== 'buyable' ? (
              <Button
                onClickCapture={onOpenNotifyMeModal}
                variant="txtSignalLime"
                onClick={onOpenNotifyMeModal}
              >
                {t('nft-details:notify-me')}
              </Button>
            ) : (
              <Button
                as={NextLink}
                variant="txtSignalLime"
                isDisabled={availability !== 'buyable'}
                onClick={onPurchaseButtonClicked}
                href={
                  campaignId
                    ? `/checkout/${currentNft.nftId}?origin=campaign&campaign-id=${campaignId}`
                    : `/checkout/${currentNft.nftId}`
                }
                passHref
              >
                {buyButtonText}
              </Button>
            )}
          </>
        ) : null}
        <Button
          as={NextLink}
          variant="txtWhite"
          href={`/nft/${currentNft.nftId}`}
          passHref
          onClick={onLearnMoreButtonClicked}
        >
          {t('campaign:learn-more')}
        </Button>
      </Flex>
    </Flex>
  )
}
